import  { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Filter, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import AnimatedProductCard from '../components/AnimatedProductCard';
import { products } from '../data';
import { Product } from '../types';

const ShopPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filteredProducts, setFilteredProducts] = useState<Product[]>(products);
  const [showFilters, setShowFilters] = useState(false);
  
  // Filter states
  const [selectedCategory, setSelectedCategory] = useState<string>(searchParams.get('category') || '');
  const [selectedMaterial, setSelectedMaterial] = useState<string>('');
  const [selectedStyle, setSelectedStyle] = useState<string>('');
  const [priceRange, setPriceRange] = useState<string>('');
  
  const categories = ['rings', 'necklaces', 'bracelets', 'earrings'];
  const materials = ['gold', 'silver', 'diamond'];
  const styles = ['classic', 'modern', 'vintage'];
  const priceRanges = [
    { label: 'Under $1,000', value: '0-1000' },
    { label: '$1,000 - $2,000', value: '1000-2000' },
    { label: '$2,000 - $3,000', value: '2000-3000' },
    { label: 'Over $3,000', value: '3000-999999' }
  ];
  
  useEffect(() => {
    const categoryParam = searchParams.get('category');
    if (categoryParam) {
      setSelectedCategory(categoryParam);
    }
    
    applyFilters();
  }, [searchParams, selectedCategory, selectedMaterial, selectedStyle, priceRange]);
  
  const applyFilters = () => {
    let filtered = [...products];
    
    if (selectedCategory) {
      filtered = filtered.filter(product => product.category === selectedCategory);
    }
    
    if (selectedMaterial) {
      filtered = filtered.filter(product => product.material === selectedMaterial);
    }
    
    if (selectedStyle) {
      filtered = filtered.filter(product => product.style === selectedStyle);
    }
    
    if (priceRange) {
      const [min, max] = priceRange.split('-').map(Number);
      filtered = filtered.filter(product => product.price >= min && product.price <= max);
    }
    
    setFilteredProducts(filtered);
  };
  
  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
    if (category) {
      searchParams.set('category', category);
    } else {
      searchParams.delete('category');
    }
    setSearchParams(searchParams);
  };
  
  const resetFilters = () => {
    setSelectedCategory('');
    setSelectedMaterial('');
    setSelectedStyle('');
    setPriceRange('');
    setSearchParams({});
  };

  return (
    <div className="pt-24 pb-16">
      <div className="container mx-auto px-4">
        <motion.div 
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="font-serif text-4xl mb-2">Our Collection</h1>
          <p className="text-gray-600">Discover our exquisite handcrafted pieces</p>
        </motion.div>
        
        <motion.div 
          className="flex justify-between items-center mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <motion.button
            className="flex items-center text-sm font-medium"
            onClick={() => setShowFilters(!showFilters)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Filter size={18} className="mr-2" />
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </motion.button>
          
          <div className="flex items-center">
            <span className="text-sm text-gray-600 mr-2">Sort by:</span>
            <div className="relative">
              <select className="appearance-none bg-white border border-gray-200 rounded px-4 py-2 pr-8 text-sm focus:outline-none focus:border-gold">
                <option>Newest</option>
                <option>Price: Low to High</option>
                <option>Price: High to Low</option>
                <option>Best Selling</option>
              </select>
              <ChevronDown size={16} className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none" />
            </div>
          </div>
        </motion.div>
        
        <AnimatePresence>
          {showFilters && (
            <motion.div 
              className="grid grid-cols-1 md:grid-cols-4 gap-6 bg-beige-light p-6 mb-8"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div>
                <h3 className="font-medium mb-3">Category</h3>
                <div className="space-y-2">
                  <div className="flex items-center">
                    <input
                      type="radio"
                      id="category-all"
                      name="category"
                      checked={selectedCategory === ''}
                      onChange={() => handleCategoryChange('')}
                      className="mr-2"
                    />
                    <label htmlFor="category-all">All Categories</label>
                  </div>
                  {categories.map(category => (
                    <div key={category} className="flex items-center">
                      <input
                        type="radio"
                        id={`category-${category}`}
                        name="category"
                        checked={selectedCategory === category}
                        onChange={() => handleCategoryChange(category)}
                        className="mr-2"
                      />
                      <label htmlFor={`category-${category}`} className="capitalize">{category}</label>
                    </div>
                  ))}
                </div>
              </div>
              
              <div>
                <h3 className="font-medium mb-3">Material</h3>
                <div className="space-y-2">
                  <div className="flex items-center">
                    <input
                      type="radio"
                      id="material-all"
                      name="material"
                      checked={selectedMaterial === ''}
                      onChange={() => setSelectedMaterial('')}
                      className="mr-2"
                    />
                    <label htmlFor="material-all">All Materials</label>
                  </div>
                  {materials.map(material => (
                    <div key={material} className="flex items-center">
                      <input
                        type="radio"
                        id={`material-${material}`}
                        name="material"
                        checked={selectedMaterial === material}
                        onChange={() => setSelectedMaterial(material)}
                        className="mr-2"
                      />
                      <label htmlFor={`material-${material}`} className="capitalize">{material}</label>
                    </div>
                  ))}
                </div>
              </div>
              
              <div>
                <h3 className="font-medium mb-3">Style</h3>
                <div className="space-y-2">
                  <div className="flex items-center">
                    <input
                      type="radio"
                      id="style-all"
                      name="style"
                      checked={selectedStyle === ''}
                      onChange={() => setSelectedStyle('')}
                      className="mr-2"
                    />
                    <label htmlFor="style-all">All Styles</label>
                  </div>
                  {styles.map(style => (
                    <div key={style} className="flex items-center">
                      <input
                        type="radio"
                        id={`style-${style}`}
                        name="style"
                        checked={selectedStyle === style}
                        onChange={() => setSelectedStyle(style)}
                        className="mr-2"
                      />
                      <label htmlFor={`style-${style}`} className="capitalize">{style}</label>
                    </div>
                  ))}
                </div>
              </div>
              
              <div>
                <h3 className="font-medium mb-3">Price</h3>
                <div className="space-y-2">
                  <div className="flex items-center">
                    <input
                      type="radio"
                      id="price-all"
                      name="price"
                      checked={priceRange === ''}
                      onChange={() => setPriceRange('')}
                      className="mr-2"
                    />
                    <label htmlFor="price-all">All Prices</label>
                  </div>
                  {priceRanges.map(range => (
                    <div key={range.value} className="flex items-center">
                      <input
                        type="radio"
                        id={`price-${range.value}`}
                        name="price"
                        checked={priceRange === range.value}
                        onChange={() => setPriceRange(range.value)}
                        className="mr-2"
                      />
                      <label htmlFor={`price-${range.value}`}>{range.label}</label>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="md:col-span-4 flex justify-end">
                <motion.button
                  onClick={resetFilters}
                  className="text-sm text-gold hover:underline"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Reset Filters
                </motion.button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        
        <AnimatePresence>
          {filteredProducts.length > 0 ? (
            <motion.div 
              className="grid grid-cols-1 md:grid-cols-3 gap-8"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              layout
            >
              {filteredProducts.map((product, index) => (
                <AnimatedProductCard key={product.id} product={product} index={index} />
              ))}
            </motion.div>
          ) : (
            <motion.div 
              className="text-center py-16"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <h3 className="font-serif text-2xl mb-2">No products found</h3>
              <p className="text-gray-600 mb-8">Try adjusting your filters to find what you're looking for.</p>
              <motion.button
                onClick={resetFilters}
                className="inline-block border border-gold text-gold hover:bg-gold hover:text-white px-6 py-2 text-sm"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Clear Filters
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default ShopPage;
 